import { Points } from 'components/Points/Points';

// The notice on a trade-offer leg that has not opened yet (pending_funding).
// The class is styled by each card's own stylesheet (TradeCard.css,
// ReceivingTradeCard.css); this file owns only the copy.
//
// reservedPoints is what the leg's receiver had set aside at accept (their net
// on the offer). 0 or absent means nothing was reserved: the receiver is the
// net creditor, or the backend predates the field.

interface SendingProps {
  traderName: string;
  fundedByTradeNumber?: string | null;
  reservedPoints?: number;
}

/** Sending card: the viewer ships this leg once it opens. */
export const SendingFundingNotice = ({ traderName, fundedByTradeNumber, reservedPoints = 0 }: SendingProps) => {
  const fundingTrade = fundedByTradeNumber ? `Trade #${fundedByTradeNumber}` : 'its funding trade';
  return (
    <div className="awaiting-funding-notice" data-testid="awaiting-funding-notice">
      This trade opens automatically once {fundingTrade} completes and pays {traderName}.{' '}
      {reservedPoints > 0 ? (
        <>
          {traderName} already has <Points type="text" points={reservedPoints} /> set aside for it; the rest comes
          from {fundingTrade}.
        </>
      ) : (
        'Those points fund this trade.'
      )}{' '}
      Do not ship these cards until this trade opens.
    </div>
  );
};

interface ReceivingProps {
  fundedByTradeNumber?: string | null;
  reservedPoints?: number;
  totalValue: number;
}

/** Receiving card: the viewer is the leg's receiver, so the reserve is theirs. */
export const ReceivingFundingNotice = ({ fundedByTradeNumber, reservedPoints = 0, totalValue }: ReceivingProps) => {
  const fundingTrade = fundedByTradeNumber ? `Trade #${fundedByTradeNumber}` : 'funding trade';
  // An admin removing items from a waiting leg can leave the reserve above the
  // leg's value until it opens; the backend refunds the excess then.
  const remainder = Math.max(0, totalValue - reservedPoints);
  return (
    <div className="awaiting-funding-notice" data-testid="awaiting-funding-notice">
      This trade opens automatically once your {fundingTrade} arrives and pays you.{' '}
      {reservedPoints > 0 ? (
        <>
          <Points type="text" points={reservedPoints} /> from your balance are set aside for this trade; the
          remaining <Points type="text" points={remainder} /> come from{' '}
          {fundedByTradeNumber ? fundingTrade : 'your funding trade'} when it pays you.
        </>
      ) : (
        'Those points fund the cards coming to you.'
      )}
    </div>
  );
};
